import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { Globals } from "../types/globals";
import { initService } from "helpers/initService";

interface NavigationState {
  currentModule: string | null;
  currentPath: string | null;
  navigationHistory: Array<{ module: string; path: string }>;
}

class RoutingService {
  private readonly MAX_HISTORY_SIZE = 10;
  private readonly _state$ = new BehaviorSubject<NavigationState>({
    currentModule: null,
    currentPath: null,
    navigationHistory: [],
  });

  public readonly state$ = this._state$.asObservable();

  public readonly currentModule$ = this._state$.pipe(
    map((state) => state.currentModule),
    distinctUntilChanged(),
  );

  public updateModuleNavigation(moduleName: Globals.ModuleNames, fullPath: string) {
    const current = this._state$.value;
    const { navigationHistory } = current;

    const firstPathPartCurrent = current.currentPath?.split("/")?.[2] ?? "";
    const firstPathPartNew = fullPath.split("/")?.[2] ?? "";

    if (
      (current.currentModule && current.currentModule !== moduleName) ||
      firstPathPartCurrent !== firstPathPartNew
    ) {
      const newHistory = [
        ...navigationHistory,
        { module: current.currentModule!, path: current.currentPath! },
      ].slice(-this.MAX_HISTORY_SIZE);

      this._state$.next({
        currentModule: moduleName,
        currentPath: fullPath,
        navigationHistory: newHistory,
      });
    } else {
      this._state$.next({
        ...current,
        currentModule: moduleName,
        currentPath: fullPath,
      });
    }
  }

  public getModuleReferrerPath(routesToIgnore: string[] = []): string | null {
    const { navigationHistory } = this._state$.value;
    const prevRoutes = routesToIgnore
      ? navigationHistory.filter(
          (route) =>
            route.path !== window.location.pathname && !routesToIgnore.find((a) => route?.path?.includes(a)),
        )
      : navigationHistory;
    if (!prevRoutes.length) return null;

    const previousEntry = prevRoutes[prevRoutes.length - 1];
    return previousEntry?.path ?? null;
  }
}

let _routingService: Readonly<RoutingService>;

export const getRoutingService = () => {
  if (!_routingService) {
    _routingService = initService(() => new RoutingService());
  }
  return _routingService;
};
